// Which files an upload field takes, as its label promises ("JPG / PNG / PDF"). The file picker's
// `accept` is only a hint (it can be switched to "All files", a drop skips it, and "image/*" lets WebP
// and GIF through), so the page checks the picked file itself before anything is uploaded.

export interface FileRule {
  /** Lower-case extensions without the dot. Used when the browser reports no type for the file. */
  extensions: readonly string[];
  /** Accepted MIME types, or a family such as "image/*". */
  mimes: readonly string[];
  /** The file picker's `accept` attribute. */
  accept: string;
  /** How the allowed types read in the message, e.g. "JPG, PNG or PDF". */
  label: string;
}

const DOCX = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const FILE_RULES = {
  /** Scans of identity and bank documents: "JPG / PNG / PDF". */
  scan: {
    extensions: ["jpg", "jpeg", "png", "pdf"],
    mimes: ["image/jpeg", "image/png", "application/pdf"],
    accept: ".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf",
    label: "JPG, PNG or PDF",
  },
  /** A résumé: "PDF or DOCX". Worded "Word" in the message because it is also spoken, and "DOCX" reads badly aloud. */
  resume: {
    extensions: ["pdf", "docx"],
    mimes: ["application/pdf", DOCX],
    accept: `.pdf,.docx,application/pdf,${DOCX}`,
    label: "PDF or Word",
  },
  /** Certificates and statutory forms: "PDF or image". */
  pdfOrImage: {
    extensions: ["pdf", "jpg", "jpeg", "png", "gif", "webp", "heic", "heif", "bmp"],
    mimes: ["application/pdf", "image/*"],
    accept: "image/*,.pdf",
    label: "PDF or image",
  },
} as const satisfies Record<string, FileRule>;

export type FileRuleKey = keyof typeof FILE_RULES;

const mimeMatches = (type: string, mime: string): boolean =>
  mime.endsWith("/*") ? type.startsWith(mime.slice(0, -1)) : type === mime;

/** What the employee is told (and Sirra says) about a file of the wrong type. */
export const wrongFileTypeText = (rule: FileRule): string =>
  `That's not the correct file type. Please upload a ${rule.label} file.`;

/** null when the file is an accepted type, otherwise what to tell the employee. */
export function wrongFileTypeMessage(file: Pick<File, "name" | "type">, rule: FileRule): string | null {
  const type = file.type.toLowerCase();
  const ext = file.name.includes(".") ? file.name.split(".").pop()!.toLowerCase() : "";
  // The browser's own type is the better evidence; the extension decides only when it reports none.
  const ok = type ? rule.mimes.some((m) => mimeMatches(type, m)) : rule.extensions.includes(ext);
  return ok ? null : wrongFileTypeText(rule);
}
